import {
    type Image as ImageType,
    type Product,
    type ProductSearchImageItem,
    type Video,
} from '../../../shared/types';

type GalleryImageInput = ImageType | ProductSearchImageItem;

type GalleryVideoSource = {
    src: string;
    type: string;
};

type GalleryMediaImageItem = {
    kind: 'image';
    key: string;
    alt: string;
    src: string;
    srcset: string;
    sizes: string;
    thumbnail: string;
    trackingUrl: string;
};

type GalleryMediaVideoItem = {
    kind: 'video';
    key: string;
    alt: string;
    src: string;
    poster: Video['poster'];
    thumbnail: string;
    sources: GalleryVideoSource[];
    trackingUrl: string;
    durationInSeconds: number;
};

type GalleryMediaItem = GalleryMediaImageItem | GalleryMediaVideoItem;

function toVideoSources(
    sources: Video['sources'],
    sourceUrl: string,
    mimeType: string,
): GalleryVideoSource[] {
    if (Array.isArray(sources) && sources.length > 0) {
        return sources;
    }

    return [
        {
            src: sourceUrl,
            type: mimeType || 'video/mp4',
        },
    ];
}

function toImageMediaItem(image: GalleryImageInput, position: number): GalleryMediaImageItem {
    return {
        kind: 'image',
        key: `image-${position}-${image.src}`,
        alt: image.alt,
        src: image.src,
        srcset: image.srcset,
        sizes: image.sizes,
        thumbnail: 'thumbnail' in image && image.thumbnail ? image.thumbnail : image.src,
        trackingUrl: image.src,
    };
}

function toVideoMediaItem(video: Video, position: number): GalleryMediaVideoItem {
    const primarySource = video.src;
    const thumbnail = (video.thumbnail || video.previewSrc) ?? '';
    const sources = toVideoSources(video.sources, primarySource, video.mimeType);

    return {
        kind: 'video',
        key: `video-${position}-${primarySource}`,
        alt: 'Video',
        src: primarySource,
        poster: video.poster,
        thumbnail,
        sources,
        trackingUrl: primarySource,
        durationInSeconds: video.durationInSeconds || 0,
    };
}

export function buildGalleryMediaItems(
    mediaContent: Product['mediaContent'] | ProductSearchImageItem[],
): GalleryMediaItem[] {
    return mediaContent.map((item, index) =>
        'mimeType' in item ? toVideoMediaItem(item, index) : toImageMediaItem(item, index),
    );
}

export type { GalleryMediaItem };
